import React, { useState } from "react";
import SubPageLayout from "../../../components/shared/SubPageLayout";

const PayrollTax = () => {
  const breadcrumbItems = [
    { label: "Administrative", link: "/administrative" },
    { label: "Payroll & Tax", link: "/administrative/payroll-tax" },
  ];

  // State for active tab
  const [activeTab, setActiveTab] = useState("dashboard");

  // State for payroll metrics
  const [payrollMetrics] = useState({
    totalPayroll: 125000,
    employeeCount: 48,
    nextPayrollDate: "2025-04-15",
    pendingApprovals: 3,
  });

  // State for employee data
  const [employees] = useState([
    {
      id: 1,
      name: "John Doe",
      employeeId: "EMP001",
      department: "Sales",
      grossPay: 5000,
      deductions: 1200,
      netPay: 3800,
      status: "processed",
    },
    {
      id: 2,
      name: "Jane Smith",
      employeeId: "EMP002",
      department: "Marketing",
      grossPay: 4500,
      deductions: 1000,
      netPay: 3500,
      status: "pending",
    },
    // Add more employee data as needed
  ]);

  // State for tax overview
  const [taxOverview] = useState({
    totalTaxesOwed: 45000,
    upcomingDeadlines: [
      { id: 1, form: "W-2", dueDate: "2025-01-31" },
      { id: 2, form: "1099-MISC", dueDate: "2025-01-31" },
      { id: 3, form: "941", dueDate: "2025-04-30" },
    ],
    recentFilings: [
      { id: 1, form: "941", date: "2025-01-31", status: "completed" },
      { id: 2, form: "W-2", date: "2025-01-15", status: "completed" },
    ],
  });

  // State for modals
  const [showProcessPayrollModal, setShowProcessPayrollModal] = useState(false);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [showGenerateReportModal, setShowGenerateReportModal] = useState(false);

  // Function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Function to format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Render the Payroll Dashboard tab
  const renderPayrollDashboard = () => (
    <div className="space-y-6">
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
            Total Payroll Amount
          </h3>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
            {formatCurrency(payrollMetrics.totalPayroll)}
          </p>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
            Number of Employees
          </h3>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
            {payrollMetrics.employeeCount}
          </p>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
            Next Payroll Date
          </h3>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
            {formatDate(payrollMetrics.nextPayrollDate)}
          </p>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
            Pending Approvals
          </h3>
          <p className="mt-2 text-3xl font-bold text-red-600">
            {payrollMetrics.pendingApprovals}
          </p>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Recent Activity</h2>
        </div>
        <div className="p-6">
          <div className="flow-root">
            <ul className="-mb-8">
              <li className="relative pb-8">
                <div className="relative flex space-x-3">
                  <div>
                    <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white">
                      <svg
                        className="h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </div>
                  <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-500">
                        Processed payroll for{" "}
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          March 31, 2025
                        </span>
                      </p>
                    </div>
                    <div className="text-right text-sm whitespace-nowrap text-gray-500 dark:text-gray-500">
                      <time dateTime="2025-03-31">March 31, 2025</time>
                    </div>
                  </div>
                </div>
              </li>
              {/* Add more activity items as needed */}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Render the Employee Payroll Management tab
  const renderEmployeePayroll = () => (
    <div className="space-y-6">
      {/* Search and Filter Controls */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative">
              <input
                type="text"
                placeholder="Search employees..."
                className="block w-64 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-[#202EC6] focus:ring-[#202EC6] sm:text-sm placeholder-gray-400 dark:placeholder-gray-500"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <select className="block w-48 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-[#202EC6] focus:ring-[#202EC6] sm:text-sm">
              <option value="" className="dark:bg-gray-700">All Departments</option>
              <option value="sales" className="dark:bg-gray-700">Sales</option>
              <option value="marketing" className="dark:bg-gray-700">Marketing</option>
              <option value="finance" className="dark:bg-gray-700">Finance</option>
              <option value="hr" className="dark:bg-gray-700">HR</option>
            </select>
          </div>
          <button
            onClick={() => setShowAddEmployeeModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#029831] hover:bg-[#018529] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#029831] dark:focus:ring-offset-gray-800"
          >
            <svg className="mr-2 -ml-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add Employee
          </button>
        </div>
      </div>

      {/* Employee Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Employee
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Department
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Gross Pay
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Deductions
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Net Pay
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {employees.map((employee) => (
                <tr key={employee.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {employee.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {employee.employeeId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {employee.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatCurrency(employee.grossPay)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatCurrency(employee.deductions)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatCurrency(employee.netPay)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        employee.status === "processed"
                          ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                          : "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200"
                      }`}
                    >
                      {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-[#202EC6] dark:text-[#4A5CFF] hover:text-opacity-80 dark:hover:text-opacity-80">
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render the Tax Filing Tools tab
  const renderTaxTools = () => (
    <div className="space-y-6">
      {/* Tax Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
            Total Taxes Owed
          </h3>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
            {formatCurrency(taxOverview.totalTaxesOwed)}
          </p>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
            Upcoming Deadlines
          </h3>
          <div className="mt-2 space-y-2">
            {taxOverview.upcomingDeadlines.map((deadline) => (
              <div
                key={deadline.id}
                className="flex justify-between items-center"
              >
                <span className="text-sm text-gray-900 dark:text-gray-100">{deadline.form}</span>
                <span className="text-sm text-gray-500 dark:text-gray-500">
                  {formatDate(deadline.dueDate)}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">Recent Filings</h3>
          <div className="mt-2 space-y-2">
            {taxOverview.recentFilings.map((filing) => (
              <div
                key={filing.id}
                className="flex justify-between items-center"
              >
                <span className="text-sm text-gray-900 dark:text-gray-100">{filing.form}</span>
                <span
                  className={`text-sm ${
                    filing.status === "completed"
                      ? "text-green-600"
                      : "text-yellow-600"
                  }`}
                >
                  {filing.status.charAt(0).toUpperCase() +
                    filing.status.slice(1)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tax Forms Repository */}
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Tax Forms Repository
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {["W-2", "1099-MISC", "941", "940", "W-4", "I-9"].map((form) => (
              <div key={form} className="border rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{form}</h3>
                <div className="mt-4 flex space-x-3">
                  <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-surface-default dark:bg-surface-default hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202EC6]">
                    Download
                  </button>
                  <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-surface-default dark:bg-surface-default hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202EC6]">
                    Upload
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Render the Reporting & Analytics tab
  const renderReporting = () => (
    <div className="space-y-6">
      {/* Report Generation Tools */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Generate Reports
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <button
            onClick={() => setShowGenerateReportModal(true)}
            className="relative block w-full border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg p-12 text-center hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202EC6] dark:focus:ring-offset-gray-800"
          >
            <svg
              className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-gray-100">
              Create New Report
            </span>
          </button>
        </div>
      </div>

      {/* Analytics Charts */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Payroll Analytics
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Payroll Trends Chart */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
                Payroll Trends
              </h3>
              <div className="aspect-w-16 aspect-h-9 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                    />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Payroll trend visualization will be displayed here
                  </p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Total Payroll</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {formatCurrency(payrollMetrics.totalPayroll)}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Avg. Salary</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {formatCurrency(payrollMetrics.totalPayroll / payrollMetrics.employeeCount)}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Growth</p>
                  <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                    +5.2%
                  </p>
                </div>
              </div>
            </div>

            {/* Department Distribution Chart */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
                Department Distribution
              </h3>
              <div className="aspect-w-16 aspect-h-9 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
                    />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Department distribution chart will be displayed here
                  </p>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-[#202EC6] rounded-full"></div>
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Sales</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">32%</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-[#029831] rounded-full"></div>
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Marketing</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">28%</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-[#F59E0B] rounded-full"></div>
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Finance</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">24%</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-[#EF4444] rounded-full"></div>
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">HR</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">16%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Reports */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Recent Reports</h2>
        </div>
        <div className="p-6">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {[
              { id: 1, name: "Payroll Summary Q1 2025", date: "2025-03-31", type: "PDF", size: "2.4 MB" },
              { id: 2, name: "Tax Liability Report March 2025", date: "2025-03-31", type: "Excel", size: "1.8 MB" },
              { id: 3, name: "Employee Benefits Report", date: "2025-03-15", type: "CSV", size: "956 KB" },
            ].map((report) => (
              <li key={report.id} className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {report.name}
                    </p>
                    <div className="mt-1 flex items-center space-x-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(report.date)}
                      </p>
                      <span className="text-gray-300 dark:text-gray-600">•</span>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {report.type}
                      </p>
                      <span className="text-gray-300 dark:text-gray-600">•</span>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {report.size}
                      </p>
                    </div>
                  </div>
                  <button className="text-[#202EC6] hover:text-[#202EC6] dark:text-[#4A5CFF] dark:hover:text-[#6B79FF] hover:underline text-sm font-medium">
                    Download
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );

  // Process Payroll Modal
  const renderProcessPayrollModal = () =>
    showProcessPayrollModal && (
      <div className="fixed z-10 inset-0 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"></div>
          <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
            <div>
              <div className="mt-3 text-center sm:mt-5">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                  Process Payroll
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Are you sure you want to process payroll for the current period?
                  </p>
                  <div className="mt-4 text-left">
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Payroll Summary:
                      </p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Total Amount: {formatCurrency(payrollMetrics.totalPayroll)}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Employees: {payrollMetrics.employeeCount}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Pay Date: {formatDate(payrollMetrics.nextPayrollDate)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#029831] text-base font-medium text-white hover:bg-[#018529] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#029831] sm:col-start-2 sm:text-sm"
                onClick={() => setShowProcessPayrollModal(false)}
              >
                Process
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202EC6] sm:mt-0 sm:col-start-1 sm:text-sm"
                onClick={() => setShowProcessPayrollModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  // Add Employee Modal
  const renderAddEmployeeModal = () =>
    showAddEmployeeModal && (
      <div className="fixed z-10 inset-0 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"></div>
          <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
            <div>
              <div className="mt-3 text-center sm:mt-5">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                  Add New Employee
                </h3>
                <div className="mt-4">
                  <form className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          First Name
                        </label>
                        <input
                          type="text"
                          className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md shadow-sm focus:border-[#202EC6] focus:ring-[#202EC6] sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Last Name
                        </label>
                        <input
                          type="text"
                          className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md shadow-sm focus:border-[#202EC6] focus:ring-[#202EC6] sm:text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Email
                      </label>
                      <input
                        type="email"
                        className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md shadow-sm focus:border-[#202EC6] focus:ring-[#202EC6] sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Department
                      </label>
                      <select className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md shadow-sm focus:border-[#202EC6] focus:ring-[#202EC6] sm:text-sm">
                        <option>Sales</option>
                        <option>Marketing</option>
                        <option>Finance</option>
                        <option>HR</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Base Salary
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 dark:text-gray-400 sm:text-sm">$</span>
                        </div>
                        <input
                          type="text"
                          className="mt-1 block w-full pl-7 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md shadow-sm focus:border-[#202EC6] focus:ring-[#202EC6] sm:text-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#029831] text-base font-medium text-white hover:bg-[#018529] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#029831] sm:col-start-2 sm:text-sm"
                onClick={() => setShowAddEmployeeModal(false)}
              >
                Add Employee
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202EC6] sm:mt-0 sm:col-start-1 sm:text-sm"
                onClick={() => setShowAddEmployeeModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  // Generate Report Modal
  const renderGenerateReportModal = () =>
    showGenerateReportModal && (
      <div className="fixed z-10 inset-0 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"></div>
          <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
            <div>
              <div className="mt-3 text-center sm:mt-5">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                  Generate Report
                </h3>
                <div className="mt-4">
                  <form className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Report Type
                      </label>
                      <select className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md shadow-sm focus:border-[#202EC6] focus:ring-[#202EC6] sm:text-sm">
                        <option>Payroll Summary</option>
                        <option>Tax Deductions</option>
                        <option>Employee Benefits</option>
                        <option>Department Expenses</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Date Range
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <input
                            type="date"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md shadow-sm focus:border-[#202EC6] focus:ring-[#202EC6] sm:text-sm"
                          />
                        </div>
                        <div>
                          <input
                            type="date"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md shadow-sm focus:border-[#202EC6] focus:ring-[#202EC6] sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Format
                      </label>
                      <select className="mt-1 block w-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md shadow-sm focus:border-[#202EC6] focus:ring-[#202EC6] sm:text-sm">
                        <option>PDF</option>
                        <option>Excel</option>
                        <option>CSV</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Include Sections
                      </label>
                      <div className="space-y-2">
                        {['Summary', 'Detailed Breakdown', 'Charts & Graphs', 'Notes'].map((section) => (
                          <div key={section} className="flex items-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-[#202EC6] focus:ring-[#202EC6] border-gray-300 dark:border-gray-600 rounded"
                            />
                            <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                              {section}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#029831] text-base font-medium text-white hover:bg-[#018529] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#029831] sm:col-start-2 sm:text-sm"
                onClick={() => setShowGenerateReportModal(false)}
              >
                Generate
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202EC6] sm:mt-0 sm:col-start-1 sm:text-sm"
                onClick={() => setShowGenerateReportModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <SubPageLayout
      title="Payroll & Tax"
      subtitle="Manage payroll processing and tax documentation"
      breadcrumbItems={breadcrumbItems}
    >
      {/* Action Buttons */}
      <div className="mb-6 flex space-x-4">
        <button
          onClick={() => setShowProcessPayrollModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#029831] hover:bg-[#018529] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#029831]"
        >
          Process Payroll
        </button>
        <button
          onClick={() => setShowAddEmployeeModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202EC6] hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202EC6]"
        >
          Add Employee
        </button>
        <button
          onClick={() => setShowGenerateReportModal(true)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-surface-default dark:bg-surface-default hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202EC6]"
        >
          Generate Report
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-4" aria-label="Tabs">
          {[
            { id: "dashboard", name: "Payroll Dashboard" },
            { id: "employees", name: "Employee Payroll" },
            { id: "tax", name: "Tax Filing Tools" },
            { id: "reporting", name: "Reporting & Analytics" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                activeTab === tab.id
                  ? "bg-[#202EC6] text-white"
                  : "text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="py-4">
        {activeTab === "dashboard" && renderPayrollDashboard()}
        {activeTab === "employees" && renderEmployeePayroll()}
        {activeTab === "tax" && renderTaxTools()}
        {activeTab === "reporting" && renderReporting()}
      </div>

      {/* Modals */}
      {renderProcessPayrollModal()}
      {renderAddEmployeeModal()}
      {renderGenerateReportModal()}
    </SubPageLayout>
  );
};

export default PayrollTax;
